/**
 * Public Mobile Money payment page.
 *
 * This is a *shareable* URL — no auth required. Anyone with the link
 * (the customer, a friend, a relative with airtime on their MoMo
 * number) can settle the bill here.
 *
 * Today this is a **PLACEHOLDER** that simulates the MTN Mobile Money
 * "Approve on your phone" prompt. When the real integration is wired
 * the only thing that changes is the body of `submit()` — the URL,
 * routing and visual flow stay the same.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Loader2, ShieldCheck, Smartphone, CheckCircle2, Receipt, Copy, AlertTriangle } from 'lucide-react';
import { formatMoney } from '@/lib/policy';
import { toast } from 'sonner';

export default function MobileMoneyPay() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [payerPhone, setPayerPhone] = useState('');
  const [payerName, setPayerName] = useState('');
  const [step, setStep] = useState('idle'); // idle | prompting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get(`/payments/mobile-money/pay/${token}`);
        if (!live) return;
        setState({ loading: false, data, error: null });
        if (data.customer_phone_hint && !payerPhone) {
          setPayerPhone(data.customer_phone_hint);
        }
        if (data.status === 'paid') {
          setStep('success');
        }
      } catch (e) {
        if (!live) return;
        setState({ loading: false, data: null, error: e?.response?.data?.detail || 'Link not found.' });
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const data = state.data;
  const currency = data?.currency || 'USD';
  const isPaid = data?.status === 'paid' || step === 'success';

  const fmt = (v) => formatMoney(v, currency);

  const submit = async () => {
    setErrorMsg('');
    const phone = payerPhone.trim();
    if (phone.length < 7) {
      setErrorMsg('Please enter the mobile money phone number that will be charged.');
      return;
    }
    setStep('prompting');
    // Simulate the "we sent a prompt to your phone, please approve it" UX.
    // In production, replace this delay with the real MoMo Collect API
    // call + a poll loop on the link status.
    try {
      await new Promise((r) => setTimeout(r, 1800));
      const { data: result } = await api.post(`/payments/mobile-money/pay/${token}`, {
        payer_phone: phone,
        payer_name: payerName.trim() || undefined,
      });
      setState((s) => ({ ...s, data: { ...(s.data || {}), ...result, status: 'paid', payer_phone: phone } }));
      setStep('success');
    } catch (e) {
      setStep('error');
      setErrorMsg(e?.response?.data?.detail || 'Payment failed. Try again.');
    }
  };

  const copyReference = (ref) => {
    if (!ref) return;
    try {
      navigator.clipboard.writeText(ref);
      toast.success('Reference copied');
    } catch (err) { /* noop */ }
  };

  if (state.loading) {
    return <CenteredCard><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /><p className="mt-3 text-sm text-muted-foreground">Loading payment&hellip;</p></CenteredCard>;
  }

  if (state.error) {
    return (
      <CenteredCard>
        <AlertTriangle className="h-7 w-7 text-rose-500" />
        <p className="mt-3 text-base font-semibold">Payment link not available</p>
        <p className="mt-1 text-sm text-muted-foreground">{state.error}</p>
        <p className="mt-1 text-xs text-muted-foreground">Ask the restaurant to send you a fresh link.</p>
      </CenteredCard>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-background to-background py-6">
      <div className="mx-auto w-full max-w-md px-4">
        <div className="mb-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Receipt className="h-4 w-4" /> Secure mobile money payment
        </div>

        {/* Order summary */}
        <Card data-testid="mm-pay-card" className="overflow-hidden">
          <CardHeader className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
            <div className="flex items-center justify-between">
              <CardTitle className="font-display text-lg">City Grill</CardTitle>
              <Badge className="border-0 bg-white/20 text-[10px] uppercase tracking-wider text-white hover:bg-white/20">
                <Smartphone className="mr-1 h-3 w-3" /> MTN Mobile Money
              </Badge>
            </div>
            <CardDescription className="text-white/90">
              Order #{data.order_number} &middot; for {data.customer_name || 'walk-in'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-3 pt-4">
            {/* Items list */}
            <div className="space-y-1 text-sm">
              {(data.items || []).map((it, idx) => (
                <div key={idx} className="flex justify-between">
                  <span className="text-muted-foreground">{it.quantity}&times; {it.name}</span>
                  <span className="tabular">{fmt(it.line_total)}</span>
                </div>
              ))}
            </div>

            <Separator />

            {/* Totals */}
            <div className="space-y-1 text-sm">
              <Row label="Subtotal" value={fmt(data.subtotal)} muted />
              {Number(data.tax || 0) > 0 && <Row label="Tax" value={fmt(data.tax)} muted />}
              {Number(data.delivery_fee || 0) > 0 && <Row label="Delivery" value={fmt(data.delivery_fee)} muted />}
              {Number(data.tip || 0) > 0 && <Row label="Tip" value={fmt(data.tip)} muted />}
              <Row label="Amount due" value={fmt(data.total)} bold />
            </div>
          </CardContent>
        </Card>

        {/* Payment action */}
        {!isPaid && (
          <Card className="mt-3" data-testid="mm-pay-form">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Pay with Mobile Money</CardTitle>
              <CardDescription className="text-xs">
                Enter the MTN MoMo number that will be charged. You&apos;ll receive an approval prompt on that phone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label htmlFor="mm-payer-phone" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Mobile money number
                </Label>
                <Input
                  id="mm-payer-phone"
                  data-testid="mm-payer-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="e.g. +233 24 411 22 33"
                  value={payerPhone}
                  onChange={(e) => setPayerPhone(e.target.value)}
                  className="mt-1 h-11 text-base"
                  disabled={step === 'prompting'}
                />
              </div>
              <div>
                <Label htmlFor="mm-payer-name" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Your name (optional)
                </Label>
                <Input
                  id="mm-payer-name"
                  data-testid="mm-payer-name"
                  placeholder="So the restaurant knows who paid"
                  value={payerName}
                  onChange={(e) => setPayerName(e.target.value)}
                  className="mt-1 h-11 text-base"
                  disabled={step === 'prompting'}
                />
              </div>

              {errorMsg && (
                <p data-testid="mm-pay-error" className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
                  {errorMsg}
                </p>
              )}

              <Button
                data-testid="mm-pay-submit"
                size="lg"
                className="h-12 w-full bg-gradient-to-r from-amber-500 to-orange-600 text-base font-semibold text-white hover:from-amber-600 hover:to-orange-700"
                onClick={submit}
                disabled={step === 'prompting'}
              >
                {step === 'prompting' ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Waiting for approval&hellip;</>
                ) : (
                  <>Pay {fmt(data.total)}</>
                )}
              </Button>

              <p className="text-center text-[11px] leading-snug text-muted-foreground">
                <ShieldCheck className="mr-1 inline h-3 w-3" />
                Secure. By tapping Pay you authorise a one-time charge of {fmt(data.total)} from {payerPhone || 'the entered number'}.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Success state */}
        {isPaid && (
          <Card className="mt-3 border-emerald-300 bg-emerald-50" data-testid="mm-pay-success">
            <CardContent className="space-y-2 py-5 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
              <p className="text-base font-semibold text-emerald-900">Payment received!</p>
              <p className="text-xs text-emerald-800">
                The restaurant has been notified. Order #{data.order_number} is now fully paid.
              </p>
              {data.reference && (
                <div className="mx-auto inline-flex items-center gap-2 rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs">
                  <span className="text-muted-foreground">Reference</span>
                  <span className="font-mono font-semibold">{data.reference}</span>
                  <button
                    type="button"
                    onClick={() => copyReference(data.reference)}
                    className="text-emerald-700 hover:text-emerald-900"
                    data-testid="mm-copy-reference"
                    aria-label="Copy reference"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* MoMo prompt simulation dialog (shown while submitting) */}
        <Dialog open={step === 'prompting'}>
          <DialogContent data-testid="mm-prompt-dialog" className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <Smartphone className="h-4 w-4 text-primary" /> Approve on your phone
              </DialogTitle>
              <DialogDescription className="text-xs">
                We&apos;ve sent a Mobile Money prompt to <span className="font-mono">{payerPhone}</span>.
                Enter your MoMo PIN on that phone to confirm the payment of <strong>{fmt(data.total)}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center py-4">
              <Loader2 className="h-7 w-7 animate-spin text-amber-500" />
            </div>
            <DialogFooter className="text-xs text-muted-foreground">
              This dialog closes automatically when the payment is confirmed.
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <p className="mt-4 text-center text-[11px] text-muted-foreground">
          Got this link by mistake? You can safely close this page &mdash; nothing is charged until you tap Pay.
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, muted, bold }) {
  return (
    <div className={`flex items-center justify-between ${bold ? 'mt-1 border-t border-dashed border-border pt-2 text-base font-semibold' : ''}`}>
      <span className={muted ? 'text-muted-foreground' : ''}>{label}</span>
      <span className="tabular">{value}</span>
    </div>
  );
}

function CenteredCard({ children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-sm">
        <CardContent className="flex flex-col items-center justify-center py-10 text-center">
          {children}
        </CardContent>
      </Card>
    </div>
  );
}
